<template>
    <div>
        <loading :active.sync="isLoading" :can-cancel="false" :is-full-page="true"></loading>
        <confirm-box ref="alert" :no-confirm="true"></confirm-box>
        <confirm-box ref="confirm"></confirm-box>

        <div class="container col-2 d-none d-lg-block"></div>
        <div class="container col-12 col-lg-8">
            <h1>Standards Upload</h1>
            <p>This page allows for uploading new standards with a CSV file. The CSV file must have the following headings:                
                <span class="text-primary font-monospace">code</span>, <span class="text-primary font-monospace">subcode</span>, 
                <span class="text-primary font-monospace">grade_level</span>, <span class="text-primary font-monospace">strand</span>, 
                <span class="text-primary font-monospace">content</span>, and <span class="text-primary font-monospace">notes</span>. 
                <a class="ms-1" data-bs-toggle="collapse" href="#header-define" role="button" aria-expanded="false" aria-controls="header-define">
                    <i class="fa-solid fa-circle-info"></i>
                </a>
            </p>

            <div class="row">
                <div class="col">
                    <div class="collapse mb-4" id="header-define">
                        <div class="card card-body">
                            <dl class="row">
                                <dt class="col-sm-2">code</dt>
                                <dd class="col-sm-9">The standard's code. This value must match the regular expression <code>^[A-Z0-9]{1,4}\.[A-Z0-9-]{1,5}\.\d+$</code></dd>

                                <dt class="col-sm-2">subcode</dt>
                                <dd class="col-sm-9">Text</dd>

                                <dt class="col-sm-2">grade_level</dt>
                                <dd class="col-sm-9">Text</dd>

                                <dt class="col-sm-2">strand</dt>
                                <dd class="col-sm-9">Text</dd>

                                <dt class="col-sm-2">content</dt>
                                <dd class="col-sm-9">Text</dd>

                                <dt class="col-sm-2">notes</dt>
                                <dd class="col-sm-9">Any additional information</dd>
                            </dl>
                        </div>
                    </div>
                </div>
            </div>


            <div class="row mb-4">
                <div class="form-group col-12 col-md-4 col-lg-3">
                    <label for="state-select">Standard State</label>
                    <select-state v-model="state" id="state-select"></select-state>
                </div>

                <div class="form-group col-12 col-md-8 col-lg-9">
                    <label for="area-select">Standard Area <a href="javascript:void()" class="ms-1" @click="$router.push('/admin/areas');" title="Edit areas"><i class="fa-regular fa-pen-to-square"></i></a></label>
                    <select v-model="area" class="form-select" id="area-select">
                        <option v-for="a in areas" :value="a.id">{{ a.name }} ({{ a.state }}-{{ a.year }})</option>
                    </select>
                </div>
            </div>

            <div class="row mb-4">
                <div class="form-group mb-3 col-12">
                    <label for="file-upload">File Upload</label>
                    <input class="form-control" type="file" accept=".csv" ref="file" @change="load" id="file-upload">
                </div>
            </div>
            <button type="button" class="btn btn-primary mb-4" :disabled="!file || !area" @click="upload"><i class="fa-solid fa-upload"></i>&nbsp;Upload</button>

            <ul class="list-group list-group-flush mb-4">
                <li class="list-group-item" v-for="st in standards" :class="st.status ? '' : 'problem'">
                    <h5 class="h5">
                        {{ st.code }}<span v-if="st.subcode" class="text-muted">({{ st.subcode ?? '' }})</span>
                        <span class="text-primary">{{ st.strand }}</span>
                    </h5>
                    <div>{{ st.content }}</div>
                    <div v-if="st.notes" class="mt-3 p-1 border rounded">{{ st.notes }}</div>
                    <div v-for="prb in st.problems" class="text-danger"><i class="fa-solid fa-xmark me-2"></i>{{ prb }}</div>
                </li>
            </ul>

            <button class="btn btn-primary" @click="askProcess" :disabled="!standards.length"><i class="fa-solid fa-circle-arrow-right me-1"></i>Process</button>
    </div>

    </div>
</template>

<script>
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import ConfirmBox from './ConfirmBox.vue';
import SelectState from './SelectState.vue';

export default {
    components: { Loading, SelectState, ConfirmBox },

    mounted() {
        this.isLoading = true;
        axios
            .get('/api/admin/areas')
            .then(response => {
                console.log(response.data);
                this.areas = response.data;
            })
            .catch(error => {
                console.error(error);
            })
            .finally(() => {
                this.isLoading = false;
            });
    },

    methods: {
        load: function() {
            this.file = this.$refs.file.files[0];
        },
        upload: function() {
            this.isLoading = true;
            const formData = new FormData();
            formData.append('file', this.file);
            const headers = { 'Content-Type': 'multipart/form-data' };

            axios
                .post('/api/admin/standards/upload/parse', formData, { headers })
                .then(response => {
                    console.log(response.data);
                    this.standards = response.data;
                })
                .catch(error => {
                    if (error.response.status == 422) {
                        this.$refs.alert.show({
                            title: 'Parsing Failed',
                            content: error.response.data,
                            callback: () => function(){},
                        });
                        console.warn(error.response);
                    } else {
                        console.error(err);
                    }
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        askProcess: function() {
            this.$refs.confirm.show({
                title: 'Confirm Process',
                content: 'Are you sure you would like to process these standards?',
                callback: () => this.process(),
            });
        },

        process: function() {
            this.isLoading = true;
            const data = {
                standards: this.standards,
                area: this.area,
            };
            axios
                .post('/api/admin/standards/upload/process', data)
                .then(response => {
                    console.log(response.data);
                    this.standards = [];
                })
                .catch(error => {
                    console.error(error);
                })
                .finally(() => {
                    this.isLoading = false;
                });
        }
    },

    data() {
        return {
            isLoading: false,
            state: null,
            areas: [],
            area: null,
            file: null,
            standards: [],
        };
    }
}
</script>

<style>
.problem {
    background-color: LavenderBlush;
}
</style>